import React, { useEffect, useState } from 'react';
import { Upload, X, ZoomIn, Download, Trash2, Image as ImageIcon } from 'lucide-react';
import '../styles/imgSection.css';
import Apis from '../services/apis';

const AssetImagesSection = ({ docimages, assetId ,id}) => {
  const [images, setImages] = useState([])
  useEffect(() => {
    setImages(docimages);
  },[])
  const apis = new Apis();


  const [selectedImage, setSelectedImage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [ismarking, setIsmarking] = useState(false);

  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = async (files) => {
    setIsUploading(true);
  
    try {
      for (const file of files) {
        const uniqueFileName = `${Date.now()}-${file.name}`;
  
        // 1️⃣ Get presigned URL from your API
        const presignedRes = await fetch(
          `https://schedulerapi.pricingculture.com/holdings/generate-presigned-url?fileName=${uniqueFileName}&fileType=${file.type}`
        );
  
        if (!presignedRes.ok) throw new Error("Failed to get presigned URL");
  
        const { uploadUrl } = await presignedRes.json();
  
        // 2️⃣ Upload file to S3
        await fetch(uploadUrl, {
          method: "PUT",
          headers: { "Content-Type": file.type },
          body: file,
        });
  
        // 3️⃣ Save image record in your DB via backend
        const backendRes = await apis.uploadImage(assetId, {
          imageUrl: `https://pc-holding-document-bucket.s3.us-east-2.amazonaws.com/${uniqueFileName}`,
          caption: file.name,
          isPrimary: false,
        });
  
        const savedImage = backendRes.data;
  
        // 4️⃣ Update UI
        setImages((prev) => [
          ...prev,
          {
            id: savedImage.id,
            imageUrl: savedImage.imageUrl,
            caption: savedImage.caption || file.name,
            uploadedDate: new Date().toISOString().split("T")[0],
            isPrimary: savedImage.isPrimary || false,
          },
        ]);
      }
    } catch (err) {
      console.error("Upload failed", err);
    } finally {
      setIsUploading(false);
    }
  };  

  const handleDelete = (imageId) => {
    if (window.confirm('Are you sure you want to delete this image?')) {
      setImages(prev => prev.filter(img => img.id !== imageId));
    }
  };

  const handleSetPrimary = (imageId) => {
    setIsmarking(true);
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    
    const raw = JSON.stringify({
      "holdingId": id,
      "id": imageId
    });
    
    const requestOptions = {
      method: "PATCH",
      headers: myHeaders,
      body: raw,
      redirect: "follow"
    };
    
    fetch("https://schedulerapi.pricingculture.com/holdings/holding-image/primary", requestOptions)
      .then((response) => response.text())
      .then((result) => {
        setImages(prev => prev.map(img => ({
          ...img,
          isPrimary: img.id === imageId
        })));
        setIsmarking(false);
      })
      .catch((error) => {
        alert("Error setting primary image");
        setIsmarking(false);
      });

   
  };

  const handleDownload = (image) => {
    console.log('Downloading:', image.filename);
  };

  return (
    <div className="images-section">
      {/* Upload Area */}
      <div className="images-card">
        <h3 className="images-card-title">Upload Images</h3>
        
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={`upload-area ${dragActive ? 'upload-area-active' : ''}`}
        >
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileInput}
            className="upload-input"
            disabled={isUploading}
          />
          
          <div className="upload-content">
            {isUploading ? (
              <div className="upload-loading">
                <div className="upload-spinner"></div>
                <p className="upload-loading-text">Uploading images...</p>
              </div>
            )  : (
              <div className="upload-instructions">
                <div className="upload-icon-wrapper">
                  <Upload size={32} className="upload-icon-large" />
                </div>
                <p className="upload-primary-text">
                  Drop images here or click to browse
                </p>
                <p className="upload-secondary-text">
                  Supports: JPG, PNG, GIF up to 10MB each
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Images Grid */}
      <div className="images-card">
        <div className="images-card-header">
          <h3 className="images-card-title">
            Gallery ({images?.length})
          </h3>
        </div>

        {
        ismarking ? (
          <div className="upload-loading">
            <div className="upload-spinner"></div>
            <p className="upload-loading-text">Marking Primary...</p>
          </div>
        ):
        images?.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">
              <ImageIcon size={32} />
            </div>
            <p className="empty-state-text">No images uploaded yet</p>
          </div>
        ) : (
          <div className="images-grid">
            {images?.map((image) => (
              <div key={image.id} className="image-item">
                {/* Primary Badge */}
                {image.isPrimary && (
                  <div className="primary-badge">Primary</div>
                )}

                {/* Image */}
                <div className="image-thumbnail">
                  <img
                    src={image.imageUrl}
                    alt={image.caption}
                    className="thumbnail-image"
                  />
                </div>
                
                {/* Overlay on Hover */}
                <div className="image-overlay">
                  <button
                    onClick={() => setSelectedImage(image)}
                    className="overlay-button"
                    title="View full size"
                  >
                    <ZoomIn size={20} />
                  </button>
                  {/* <button
                    onClick={() => handleDownload(image)}
                    className="overlay-button"
                    title="Download"
                  >
                    <Download size={20} />
                  </button> */}
                  {!image.isPrimary && (
                    <button
                      onClick={() => handleSetPrimary(image.id)}
                      className="overlay-button"
                      title="Set as primary"
                    >
                      <ImageIcon size={20} />
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(image.id)}
                    className="overlay-button overlay-button-delete"
                    title="Delete"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>

                
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Image Viewer Modal */}
      {selectedImage && (
        <div className="image-viewer-modal" onClick={() => setSelectedImage(null)}>
          <div className="image-viewer-container" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setSelectedImage(null)}
              className="viewer-close-button"
            >
              <X size={24} />
            </button>
            
            <div className="viewer-content">
              <img
                src={selectedImage.imageUrl}
                alt={selectedImage.caption}
                className="viewer-image"
              />
              <div className="viewer-info">
                <p className="viewer-filename">{selectedImage.filename}</p>
                <div className="viewer-meta">
                  <span>Uploaded: {selectedImage.uploadedDate}</span>
                  <span>Size: {selectedImage.size}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetImagesSection;